import React from 'react'

import {
  AvailabilityFilter,
  ClientHomeAddress,
  InPersonMeetingFormat,
  MeetingFormatChoice,
  SchedulerParticipant,
} from '@/components/integration/nylas/scheduler/types'
import { MeetingFormat } from '@/types/misc'

export interface SchedulerTimeRange {
  start: Date
  end: Date
}

export interface SchedulerSelectionContextValue {
  selectedDate: Date | null
  selectedDateTime: Date | null
  selectedDateRange: SchedulerTimeRange
  showFullCalendar: boolean
  guests: SchedulerParticipant[]
  finalPrimaryParticipant: SchedulerParticipant
  /** How to meet, as chosen with the format buttons. */
  selectedFormatChoice: MeetingFormatChoice | null
  /** Which in-person format, once "in person" is chosen and "Where?" answered. */
  selectedInPersonFormat: InPersonMeetingFormat | null
  /**
   * The format the meeting will be booked in. Null until the choices above
   * settle on one, e.g. while "Where?" is unanswered.
   */
  selectedFormat: MeetingFormat | null
  /**
   * The office chosen for an in-person office meeting, or the office handling
   * a visit to the client's home.
   */
  selectedLocationId: number | null
  clientHomeAddress: ClientHomeAddress
  /** Whose availability to show: the chosen office's advisers, or everyone. */
  availabilityFilter: AvailabilityFilter
  isExtendedAvailabilityReady: boolean

  selectDate: (date: Date | null) => void
  selectDateTime: (dateTime: Date | null) => void
  setSelectedDateRange: (range: SchedulerTimeRange) => void
  switchToCalendar: () => void
  switchToSimple: () => void
  setGuests: (guests: SchedulerParticipant[]) => void
  setFinalPrimaryParticipant: (participant: SchedulerParticipant) => void
  setSelectedFormatChoice: (formatChoice: MeetingFormatChoice | null) => void
  setSelectedInPersonFormat: (
    inPersonFormat: InPersonMeetingFormat | null,
  ) => void
  setSelectedLocationId: (locationId: number | null) => void
  setClientHomeAddress: (clientHomeAddress: ClientHomeAddress) => void
}

export const SchedulerSelectionContext =
  React.createContext<SchedulerSelectionContextValue | null>(null)

export function useSchedulerSelection() {
  const context = React.useContext(SchedulerSelectionContext)

  if (context === null) {
    throw new Error(
      'useSchedulerSelection must be used inside a SchedulerSelectionContext.Provider',
    )
  }

  return context
}
